import type { CreateStartupFormValues } from '../schema';
import { parseSelfIdAnswer } from '../self-identification';

type DraftValues = Partial<CreateStartupFormValues>;

/**
 * The values a pre-v3 draft may still hold: the self-identification answers
 * were checkbox booleans before they became 3-value answers (UX sheet r113-r115),
 * and "Do you have a lead investor?" did not exist (it was implied by a name).
 */
type LegacyDraftValues = Omit<DraftValues, 'founderFemale' | 'founderUnderrepresented'> & {
  founderFemale?: unknown;
  founderUnderrepresented?: unknown;
};

/**
 * A ticked legacy checkbox meant "yes"; an unticked one only meant the founder
 * never ticked it, which is not the same as answering "no" - so it becomes
 * unanswered. A valid new-shape answer passes through; anything else (a
 * corrupted or unknown value) is dropped rather than sent to the API.
 */
function migrateAnswer(value: unknown): DraftValues['founderFemale'] {
  if (value === true) return 'yes';
  if (value === false) return undefined;
  return parseSelfIdAnswer(value);
}

/**
 * Brings one persisted draft's values up to the current form shape, so an old
 * draft resumed after this release never sends a boolean (the create DTO now
 * rejects one with a 400).
 *
 * Args: values - the draft values exactly as they were persisted.
 * Returns: the same values with the legacy fields mapped.
 */
export function migrateLegacyDraftValues(values: LegacyDraftValues): DraftValues {
  const founderFemale = migrateAnswer(values.founderFemale);
  const founderUnderrepresented = migrateAnswer(values.founderUnderrepresented);
  const migrated: DraftValues = { ...values, founderFemale, founderUnderrepresented };

  // Categories were only collectable under a ticked box; any left behind an
  // unticked one are stale and would otherwise show as ticked with no answer.
  if (founderUnderrepresented !== 'yes') {
    delete migrated.founderUnderrepresentedCategories;
  }
  // The old wizard implied a lead investor from a typed name.
  if (migrated.hasLeadInvestor === undefined && values.leadInvestorName?.trim()) {
    migrated.hasLeadInvestor = 'yes';
  }
  return migrated;
}
